import React, { useState, useEffect } from "react";
import { Timeline, Typography, Tag, Card, Badge, Tooltip, Alert, message, Button, Space, Spin, Empty, Modal } from "antd";
import { CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, InfoCircleOutlined, ArrowRightOutlined, CheckOutlined, CloseOutlined, FlagOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { flightOperationTransactionService } from "../../../../api/enhancedApi";
import { TRANSACTION_STATUS_COLORS, TRANSACTION_STATUS_LABELS } from "../constants";

const { Text, Title } = Typography;

/**
 * 事务历史链路组件
 * 展示事务的完整溯源历史，包括迁移过程和状态变更
 */
const TransactionHistoryChain = ({ transactionType, transactionId, flightId, visible = false, onRefresh, onRejectAndClose }) => {
    const [loading, setLoading] = useState(false);
    const [historyChain, setHistoryChain] = useState(null);
    const [error, setError] = useState(null);

    // 加载事务历史链路
    const loadHistoryChain = async () => {
        if (!transactionType || !transactionId || !visible) return;

        setLoading(true);
        setError(null);

        try {
            const response = await flightOperationTransactionService.getTransactionHistoryChain(transactionType, transactionId);
            setHistoryChain(response.data || response);
        } catch (err) {
            console.error("加载事务历史链路失败:", err);
            const errorMessage = err.response?.data?.detail || err.response?.data?.message || err.message || "加载失败";
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // 当组件可见时加载数据
    useEffect(() => {
        if (visible) {
            loadHistoryChain();
        }
    }, [transactionType, transactionId, visible]);

    // 确认事务迁移
    const handleConfirmMigration = async (transactionId) => {
        try {
            const response = await flightOperationTransactionService.confirmTransactionMigration(transactionType, transactionId);

            // 根据API规范检查响应
            if (response.data && response.data.success) {
                message.success(response.data.message || "事务迁移已确认");
                loadHistoryChain(); // 重新加载溯源数据

                // 刷新主列表数据，确保状态更新
                if (onRefresh) {
                    onRefresh();
                }
            } else {
                message.error(response.data?.message || "确认失败");
            }
        } catch (err) {
            console.error("确认事务迁移失败:", err);
            const errorMessage = err.response?.data?.detail || err.response?.data?.message || err.message || "确认失败";
            message.error(errorMessage);
        }
    };

    // 拒绝事务迁移
    const handleRejectMigration = async (transactionId) => {
        // 先显示确认对话框，告知用户影响
        Modal.confirm({
            title: '确认拒绝事务迁移',
            content: (
                <div>
                    <p>拒绝迁移将会：</p>
                    <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
                        <li>清除当前事务数据</li>
                        <li>关闭编辑窗口</li>
                        <li>恢复到可重新发起事务的状态</li>
                    </ul>
                    <p style={{ color: '#ff4d4f', fontWeight: 'bold' }}>此操作不可撤销，请确认是否继续？</p>
                </div>
            ),
            okText: '确认拒绝',
            cancelText: '取消',
            okType: 'danger',
            onOk: async () => {
                try {
                    const response = await flightOperationTransactionService.rejectTransactionMigration(transactionType, transactionId);

                    // 根据API规范检查响应
                    if (response.data && response.data.success) {
                        message.success(response.data.message || "事务迁移已拒绝");

                        // 立即刷新数据并关闭Modal
                        if (onRefresh) {
                            onRefresh(); // 刷新主列表数据
                        }

                        // 通知父组件关闭Modal
                        if (onRejectAndClose) {
                            onRejectAndClose();
                        }
                    } else {
                        message.error(response.data?.message || "拒绝失败");
                    }
                } catch (err) {
                    console.error("拒绝事务迁移失败:", err);
                    const errorMessage = err.response?.data?.detail || err.response?.data?.message || err.message || "拒绝失败";
                    message.error(errorMessage);
                }
            }
        });
    };

    // 获取迁移状态标签
    const getMigrationStatusTag = (migrationStatus) => {
        const statusConfig = {
            PENDING_REVIEW: { color: "orange", text: "待检查", icon: <ClockCircleOutlined /> },
            CONFIRMED: { color: "green", text: "已确认", icon: <CheckCircleOutlined /> },
            REJECTED: { color: "red", text: "已拒绝", icon: <CloseCircleOutlined /> },
        };

        const config = statusConfig[migrationStatus] || { color: "default", text: migrationStatus, icon: <InfoCircleOutlined /> };

        return (
            <Tag color={config.color} icon={config.icon}>
                {config.text}
            </Tag>
        );
    };

    // 渲染事务项
    const renderTransactionItem = (transaction, isCurrent = false, showMigrationActions = false) => {
        const statusColor = TRANSACTION_STATUS_COLORS[transaction.status] || "default";
        const statusLabel = TRANSACTION_STATUS_LABELS[transaction.status] || transaction.status;

        return (
            <Card size="small" className={`transaction-history-item ${isCurrent ? "current-transaction" : ""}`} style={{ marginBottom: 8 }}>
                <div className="transaction-item-header">
                    <Space>
                        <Text strong>ID: {transaction.id}</Text>
                        {transaction.flight_number && <Text type="secondary">航班: {transaction.flight_number}</Text>}
                        {transaction.flight_date && <Text type="secondary">日期: {dayjs(transaction.flight_date).format("YYYY-MM-DD")}</Text>}
                        {isCurrent && <Badge status="processing" text="当前事务" />}
                    </Space>
                </div>

                <div className="transaction-item-content">
                    <Space direction="vertical" size="small" style={{ width: "100%" }}>
                        <div>
                            <Text>状态: </Text>
                            <Tag color={statusColor}>{statusLabel}</Tag>
                            {transaction.migration_status && (
                                <>
                                    <Text> 迁移状态: </Text>
                                    {getMigrationStatusTag(transaction.migration_status)}
                                </>
                            )}
                        </div>

                        {transaction.remarks && (
                            <div>
                                <Text type="secondary">备注: {transaction.remarks}</Text>
                            </div>
                        )}

                        {transaction.replaced_reason && (
                            <div>
                                <Text type="secondary">替换原因: {transaction.replaced_reason}</Text>
                            </div>
                        )}

                        <div>
                            <Text type="secondary">创建时间: {dayjs(transaction.created_at).format("YYYY-MM-DD HH:mm:ss")}</Text>
                            {transaction.replaced_at && (
                                <Text type="secondary" style={{ marginLeft: 16 }}>
                                    替换时间: {dayjs(transaction.replaced_at).format("YYYY-MM-DD HH:mm:ss")}
                                </Text>
                            )}
                        </div>

                        {/* 迁移操作按钮 */}
                        {showMigrationActions && transaction.migration_status === "PENDING_REVIEW" && (
                            <div className="migration-actions">
                                <Space>
                                    <Button type="primary" size="small" icon={<CheckOutlined />} onClick={() => handleConfirmMigration(transaction.id)}>
                                        确认迁移
                                    </Button>
                                    <Button danger size="small" icon={<CloseOutlined />} onClick={() => handleRejectMigration(transaction.id)}>
                                        拒绝迁移
                                    </Button>
                                </Space>
                            </div>
                        )}
                    </Space>
                </div>
            </Card>
        );
    };

    // 渲染时间线
    const renderTimeline = () => {
        if (!historyChain) return null;

        const timelineItems = [];

        // 添加未来事务
        historyChain.future_chain.forEach((transaction, index) => {
            timelineItems.push({
                color: "gray",
                children: renderTransactionItem(transaction),
                dot: <ArrowRightOutlined style={{ fontSize: "16px" }} />,
            });
        });

        // 添加当前事务
        timelineItems.push({
            color: "green",
            children: renderTransactionItem(historyChain.current_transaction, true, true),
            dot: <CheckCircleOutlined style={{ fontSize: "16px" }} />,
        });

        // 添加历史事务
        historyChain.history_chain.forEach((transaction, index) => {
            timelineItems.push({
                color: "blue",
                children: renderTransactionItem(transaction),
                dot: <InfoCircleOutlined style={{ fontSize: "16px" }} />,
            });
        });
        return <Timeline items={timelineItems} />;
    };

    if (loading) {
        return (
            <div style={{ textAlign: "center", padding: "20px" }}>
                <Spin tip="加载事务历史链路..." />
            </div>
        );
    }

    if (error) {
        return (
            <Alert
                message="加载失败"
                description={error}
                type="error"
                showIcon
                action={
                    <Button size="small" onClick={loadHistoryChain}>
                        重试
                    </Button>
                }
            />
        );
    }

    if (!historyChain) {
        return <Empty description="暂无历史数据" />;
    }

    return (
        <div className="transaction-history-chain" style={{ padding: "0" }}>
            <div className="subsection-title" style={{ marginBottom: "12px", fontSize: "14px", fontWeight: "500", color: "#666", borderBottom: "1px dashed #e8e8e8", paddingBottom: "6px", display: "flex", alignItems: "center" }}>
                <FlagOutlined style={{ marginRight: "6px" }} />
                事务溯源链路
                {historyChain.total_in_chain > 0 && <Badge count={historyChain.total_in_chain} style={{ marginLeft: 8 }} />}
            </div>

            <div className="history-chain-content" style={{ marginTop: "8px", padding: "8px 0 0 8px " }}>
                {historyChain.total_in_chain > 1 ? renderTimeline() : <Alert message="无关联历史" description="此事务没有迁移历史记录" type="info" showIcon size="small" />}
            </div>
        </div>
    );
};

export default TransactionHistoryChain;
